import type { ChatMessage, ClinicUser, Conversation, ConversationSummary } from './types';

export function conversationDisplayTitle(
  conversation: ConversationSummary,
  users: readonly ClinicUser[],
  currentUserId: string,
): string {
  if (conversation.type === 'clinic') return conversation.title;
  const otherParticipantId = conversation.participantIds.find(
    (participantId) => participantId !== currentUserId,
  );
  return (
    users.find((candidate) => candidate.id === otherParticipantId)?.displayName ||
    conversation.title ||
    'Conversa direta'
  );
}

function compareMessageChronology(left: ChatMessage, right: ChatMessage): number {
  return left.sentAt.localeCompare(right.sentAt) || left.id.localeCompare(right.id);
}

export function mergeChatMessagesById(
  messages: readonly ChatMessage[],
  preferredMessages: readonly ChatMessage[],
): ChatMessage[] {
  const messageById = new Map(messages.map((message) => [message.id, message]));
  preferredMessages.forEach((message) => messageById.set(message.id, message));
  return [...messageById.values()].sort(compareMessageChronology);
}

function compareConversationRecency(left: Conversation, right: Conversation): number {
  return (
    right.lastMessageAt.localeCompare(left.lastMessageAt) ||
    left.title.localeCompare(right.title, 'pt-BR') ||
    left.id.localeCompare(right.id)
  );
}

export function mergeConversationSummaries(
  conversations: Conversation[],
  summaries: ConversationSummary[],
): Conversation[] {
  const conversationById = new Map(
    conversations.map((conversation) => [conversation.id, conversation]),
  );
  return summaries
    .map((summary) => {
      const current = conversationById.get(summary.id);
      return {
        ...current,
        ...summary,
        unreadByUser: current?.unreadByUser ?? {},
        messages: current?.messages ?? [],
      };
    })
    .sort(compareConversationRecency);
}

export function updateConversationUnreadCount(
  conversations: Conversation[],
  conversationId: string,
  userId: string,
  unreadCount: number,
): Conversation[] {
  return conversations.map((conversation) =>
    conversation.id === conversationId
      ? {
          ...conversation,
          unreadByUser: { ...conversation.unreadByUser, [userId]: unreadCount },
        }
      : conversation,
  );
}

export function updateConversationUnreadCounts(
  conversations: Conversation[],
  userId: string,
  unreadCounts: Readonly<Record<string, number>>,
): Conversation[] {
  return conversations.map((conversation) => {
    const unreadCount = unreadCounts[conversation.id];
    return unreadCount == null
      ? conversation
      : {
          ...conversation,
          unreadByUser: { ...conversation.unreadByUser, [userId]: unreadCount },
        };
  });
}
